import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const API = `${'https://shopblkmrkt.com'}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user, getAuthHeader } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true); // Initial loading only
  const [isPolling, setIsPolling] = useState(false);
  const shownRemovals = useRef(new Set());

  const fetchCart = useCallback(async (isBackgroundPoll = false) => {
    if (!user) {
      setCart({ items: [], total: 0 });
      setLoading(false);
      return;
    }
    try {
      // Only show loading spinner on initial load, not background polls
      if (!isBackgroundPoll) {
        setLoading(true);
      }
      setIsPolling(true);
      
      const response = await axios.get(`${API}/cart`, {
        headers: getAuthHeader()
      });
      
      // Check for removed items and show RED toast notifications
      if (response.data.removed_items && response.data.removed_items.length > 0) {
        response.data.removed_items.forEach(item => {
          if (!shownRemovals.current.has(item.product_id)) {
            shownRemovals.current.add(item.product_id);
            // Show prominent RED error toast for sold out items
            toast.error(`"${item.name}" was purchased by another buyer and has been removed from your cart.`, {
              duration: 6000,
              style: {
                background: '#dc2626',
                color: 'white',
                border: 'none',
                fontFamily: 'monospace',
              },
            });
            // Clear from shown after 30 seconds to allow re-showing if somehow re-added
            setTimeout(() => shownRemovals.current.delete(item.product_id), 30000);
          }
        });
      }
      
      // Smooth state update - only update if there are actual changes
      setCart(prevCart => {
        const newItems = response.data.items;
        const newTotal = response.data.total;
        
        // Check if cart actually changed to avoid unnecessary re-renders
        if (JSON.stringify(prevCart.items) === JSON.stringify(newItems) && prevCart.total === newTotal) {
          return prevCart;
        }
        return { items: newItems, total: newTotal };
      });
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      if (!isBackgroundPoll) {
        setLoading(false);
      }
      setIsPolling(false);
    }
  }, [user, getAuthHeader]);

  // Initial cart fetch
  useEffect(() => {
    fetchCart(false); // Not a background poll - show loading
  }, [fetchCart]);

  // Poll cart every 15 seconds to catch items removed by other buyers
  // Uses background polling to avoid UI flicker
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => fetchCart(true), 15000); // Background poll - no loading spinner
    return () => clearInterval(interval);
  }, [user, fetchCart]);

  const addToCart = async (productId, quantity = 1, isBidWin = false, isBuyNow = false, variantId = null, giftCardDetails = null, blkcrdDetails = null) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return false;
    }
    try {
      setLoading(true);
      const payload = { 
        product_id: productId, 
        quantity, 
        is_bid_win: isBidWin, 
        is_buy_now: isBuyNow 
      };
      if (variantId) {
        payload.variant_id = variantId;
      }
      if (giftCardDetails) {
        payload.gift_card_details = giftCardDetails;
      }
      if (blkcrdDetails) {
        payload.blkcrd_details = blkcrdDetails;
      }
      const response = await axios.post(`${API}/cart/add`, 
        payload,
        { headers: getAuthHeader() }
      );
      setCart({ items: response.data.items, total: response.data.total });
      // Don't show toast here - let the calling component handle notifications
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart', { duration: 2000 });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId, variantId = null) => {
    try {
      setLoading(true);
      // Include variant_id as query param if provided
      const url = variantId 
        ? `${API}/cart/${productId}?variant_id=${variantId}`
        : `${API}/cart/${productId}`;
      const response = await axios.delete(url, {
        headers: getAuthHeader()
      });
      setCart({ items: response.data.items, total: response.data.total });
      toast('REMOVED', { 
        duration: 1000,
        style: { background: '#ef4444', color: '#fff', fontSize: '10px', padding: '4px 10px', fontWeight: 'bold' }
      });
    } catch (error) {
      toast.error('Failed to remove item');
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      await axios.delete(`${API}/cart`, {
        headers: getAuthHeader()
      });
      setCart({ items: [], total: 0 });
    } catch (error) {
      console.error('Failed to clear cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const itemCount = cart.items.reduce((sum, item) => sum + (item.quantity || 1), 0);

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, removeFromCart, clearCart, fetchCart, itemCount }}>
      {children}
    </CartContext.Provider>
  );
};
